import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import SearchOutlined from '@ant-design/icons/SearchOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Empty from 'antd/es/empty';
import Input from 'antd/es/input';
import Skeleton from 'antd/es/skeleton';
import Space from 'antd/es/space';
import theme from 'antd/es/theme';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ContentContainer from '@components/ContentContainer';
import { GradientText } from '@components/GradientText';
import { createConversationMessage } from '@queries/conversation-messages';
import { useConversationMessagesQuery } from '@queries/conversation-messages';
import { createConversation } from '@queries/conversations';
import { useConversationsQuery } from '@queries/conversations';
import { createProject, useProjectsQuery } from '@queries/projects';
import { useSuggestedSearchesQuery } from '@queries/suggested-searches';
import { useUserQuery } from '@queries/users';
import { useFocus, useSessionUserId } from '@utils/hooks';
import { useProjectId, useTeamId } from '@utils/router';

const { Title } = Typography;

const WelcomeSection = styled.div`
  margin-bottom: ${({ theme }) => theme.token.margin * 3}px;
  text-align: center;
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ProjectSetupFormCard = styled(Card)`
  display: flex;
  flex-direction: column;
  max-width: 400px;
  width: 100%;
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 750px;
  margin: 0 auto;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 10px;
`;

const InputContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const SuggestedSearchButton = styled(Button)`
  font-size: 13px;
  height: auto;
  width: fit-content;
  max-width: 100%;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;

  & > span {
    display: contents;
  }
`;

const StyledTextArea = styled(Input.TextArea)`
  border: none;
  box-shadow: ${({ theme }) =>
    `0 1px 2px 0 ${theme.token.colorBorderSecondary}`};
  padding: 8px 12px;
  font-size: 14px;
  resize: none;
  width: 100%;
  border-radius: ${({ theme }) => theme.token.borderRadius}px;
  transition: none;

  &.ant-input-textarea-auto-size {
    min-height: 36px !important;
    max-height: 120px;
  }

  .ant-input {
    height: 36px !important;
    line-height: 20px !important;
  }

  &:focus {
    box-shadow: ${({ theme }) => `0 0 0 1px ${theme.token.colorPrimaryBorder}`};
    outline: none;
    transition: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.token.colorTextPlaceholder};
    opacity: 0.8;
  }

  // Override ant-design's default focus animation
  &.ant-input-affix-wrapper-focused,
  &.ant-input-affix-wrapper:focus,
  &.ant-input-focused,
  &.ant-input:focus {
    transition: none;
    animation: none;
  }
`;

const SearchDescription = styled(Typography.Paragraph)`
  text-align: center;
  max-width: 720px;
  margin: ${({ theme }) => theme.token.marginMD}px auto;
  color: ${({ theme }) => theme.token.colorTextSecondary};
  font-size: 14px;
  line-height: 1.5;
`;

const SuggestedSearchesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.token.marginXS}px;
  margin-top: ${({ theme }) => theme.token.marginXS}px;
`;

const SkeletonWrapper = styled.div`
  width: 100%;
  display: block;
`;

const SuggestedSearchesTitle = styled(Typography.Text)`
  display: block;
  font-weight: 500;
  color: ${({ theme }) => theme.token.colorTextSecondary};
  font-size: 14px;
`;

const TitleWithInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.token.marginXXS}px;
  margin-top: 6px;
  margin-bottom: 6px;
`;

const RecentConversationCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.token.paddingMD}px;
  background-color: ${({ theme }) => theme.token.colorBgContainer};
  border: 1px solid ${({ theme }) => theme.token.colorBorderSecondary};
  border-radius: ${({ theme }) => theme.token.borderRadiusLG}px;
  margin-top: ${({ theme }) => theme.token.marginXS}px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: ${({ theme }) => theme.token.colorPrimary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
  }
`;

const RecentConversationTitle = styled.div`
  font-weight: 500;
  font-size: 15px;
  color: ${({ theme }) => theme.token.colorText};
  margin-bottom: ${({ theme }) => theme.token.marginXS}px;
`;

const RecentConversationPreview = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.token.colorTextSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100ch;
`;

/**
 * Displays content oriented around creating a user's first Project
 */
const ProjectSetupContent = ({ teamId }: { teamId: string }) => {
  const { message } = App.useApp();

  const [projectName, setProjectName] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  const onCreateProject = async () => {
    setIsCreatingProject(true);
    const loadingMessage = message.loading('Creating project...');
    try {
      await createProject({
        name: projectName,
        teamId,
      });
      loadingMessage();
    } catch (error) {
      loadingMessage();
      void message.error('Failed to create project');
    } finally {
      setIsCreatingProject(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '3rem',
        alignItems: 'center',
      }}
    >
      <Typography.Paragraph>
        Let's get started with your first project. You can create as many of
        them as you like and they act as a way to organize information across
        your team's data and information.
      </Typography.Paragraph>
      <ProjectSetupFormCard>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Typography.Title
            level={5}
            style={{ marginBottom: '0.5rem', marginTop: '0.5rem' }}
          >
            What should we call your first project?
          </Typography.Title>
          <Typography.Text type="secondary">
            This is the name that will be displayed in your workspace, you can
            always change it later.
          </Typography.Text>
          <Input
            placeholder="Project name"
            value={projectName}
            onChange={(event) => setProjectName(event.target.value)}
          />
          <Button
            type="primary"
            style={{ width: '100%' }}
            icon={<PlusOutlined />}
            disabled={!projectName.length}
            loading={isCreatingProject}
            onClick={onCreateProject}
          >
            Create
          </Button>
        </div>
      </ProjectSetupFormCard>
    </div>
  );
};

const ProjectSelectionContent = (_props: { teamId: string }) => {
  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '2rem',
        }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Space direction="vertical" align="center">
              <Typography.Title level={4} style={{ margin: 0 }}>
                Choose a Project
              </Typography.Title>
              <Typography.Text type="secondary">
                Select a project from the menu on the left side of the screen to
                start diving into your data.
              </Typography.Text>
            </Space>
          }
        />
      </div>
    </div>
  );
};

const DefaultContent = ({
  teamId,
  projectId,
}: {
  teamId: string;
  projectId: string;
}) => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [inputRef, setInputFocus] = useFocus();
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const { token } = theme.useToken();

  // Fetch the most recent conversation for the project
  const recentConversationQuery = useConversationsQuery(
    {
      projectIds: [projectId],
      pageSize: 1,
      orderBy: 'createdAt',
      orderDirection: 'desc',
    },
    !!projectId,
  );

  const recentConversation = useMemo(() => {
    if (recentConversationQuery.data?.length) {
      return recentConversationQuery.data[0];
    }
    return null;
  }, [recentConversationQuery.data]);

  // Fetch the most recent message for the conversation
  const recentMessageQuery = useConversationMessagesQuery(
    {
      conversationIds: recentConversation
        ? [recentConversation.conversationId]
        : [],
      pageSize: 1,
      orderBy: 'createdAt',
      orderDirection: 'desc',
    },
    !!recentConversation,
  );

  const recentMessage = useMemo(() => {
    if (recentMessageQuery.data?.length) {
      return recentMessageQuery.data[0];
    }
    return null;
  }, [recentMessageQuery.data]);

  // Update debounced search query with a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // Reduced from 500ms to 300ms for more responsiveness

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch suggested searches from API using the debounced query
  const suggestedSearchesQuery = useSuggestedSearchesQuery(
    {
      query: debouncedSearchQuery,
      teamId,
      projectId,
    },
    !!teamId && !!projectId,
  );

  // Use API results if available, otherwise fallback to hardcoded suggestions
  const suggestedSearches = useMemo(() => {
    if (suggestedSearchesQuery.data && suggestedSearchesQuery.data.length > 0) {
      return suggestedSearchesQuery.data.map((item) => item.query);
    }

    // Fallback to hardcoded suggestions if API returns empty results or error
    return [
      'Analyze clinical trial data from active Phase II studies for mRNA based therapies',
      'Summarize recent publications on CRISPR-Cas9 applications',
      'Extract key insights from our lab notebooks on protein folding',
    ];
  }, [suggestedSearchesQuery.data]);

  // Focus the input on component mount
  useEffect(() => {
    setInputFocus();
  }, [setInputFocus]);

  // Handle debouncing for loading state - show loading immediately when typing
  useEffect(() => {
    // When search query changes, immediately show loading state
    if (searchQuery !== debouncedSearchQuery) {
      setIsDebouncing(true);
    }
  }, [searchQuery, debouncedSearchQuery]);

  // Handle debouncing for loading state
  useEffect(() => {
    // When loading starts, set debouncing to true
    if (suggestedSearchesQuery.isLoading || suggestedSearchesQuery.isFetching) {
      setIsDebouncing(true);

      // Keep the loading state for at least 300ms to avoid flickering
      const timer = setTimeout(() => {
        if (
          !suggestedSearchesQuery.isLoading &&
          !suggestedSearchesQuery.isFetching
        ) {
          setIsDebouncing(false);
        }
      }, 300); // Reduced from 500ms to 300ms

      return () => clearTimeout(timer);
    } else if (!isDebouncing) {
      // If not loading and not debouncing, make sure debouncing is false
      setIsDebouncing(false);
    }
  }, [
    suggestedSearchesQuery.isLoading,
    suggestedSearchesQuery.isFetching,
    isDebouncing,
  ]);

  // When loading finishes, wait a bit before removing loading state
  useEffect(() => {
    if (
      !suggestedSearchesQuery.isLoading &&
      !suggestedSearchesQuery.isFetching &&
      isDebouncing
    ) {
      const timer = setTimeout(() => {
        setIsDebouncing(false);
      }, 300); // Reduced from 500ms to 300ms

      return () => clearTimeout(timer);
    }
  }, [
    suggestedSearchesQuery.isLoading,
    suggestedSearchesQuery.isFetching,
    isDebouncing,
  ]);

  const handleSubmit = useCallback(
    async (query: string) => {
      if (!query.trim()) return;

      setIsSearching(true);
      const loadingMessage = message.loading('Processing your request...');

      try {
        // Create a new conversation
        const conversation = await createConversation({
          title:
            query.length > 30
              ? `${query.substring(0, 30).trimEnd()}...`
              : query,
          type: 'project',
          projectId,
          description: null,
        });

        // Create the first message in the conversation
        await createConversationMessage({
          conversationId: conversation.conversationId,
          content: { text: query },
          role: 'user',
          contentType: 'plaintext',
          requestedModel: 'standard',
        });

        void loadingMessage();
        void message.success('Request processed successfully');

        // Clear the input after successful submission
        setSearchQuery('');

        // Redirect to the conversation page
        navigate(
          `/team/${teamId}/project/${projectId}/conversations/${conversation.conversationId}`,
        );
      } catch (error) {
        void loadingMessage();
        void message.error('Failed to process your request');
      } finally {
        setIsSearching(false);
        // Focus back on the input after submission
        setInputFocus();
      }
    },
    [message, projectId, navigate, teamId, setInputFocus],
  );

  const handleSuggestedSearchClick = useCallback(
    (suggestion: string) => {
      setSearchQuery(suggestion);
      void handleSubmit(suggestion);
    },
    [handleSubmit],
  );

  // Filter suggestions based on search query for autocomplete
  const filteredSuggestions = useMemo(() => {
    if (!searchQuery) return suggestedSearches;

    return suggestedSearches.filter((suggestion) =>
      suggestion.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, suggestedSearches]);

  // Determine if we're in a loading state (either actually loading or in debounce period)
  // Show loading state immediately when typing for autocomplete
  const isLoading =
    suggestedSearchesQuery.isLoading ||
    suggestedSearchesQuery.isFetching ||
    isDebouncing ||
    (searchQuery !== debouncedSearchQuery && searchQuery.length > 0);

  return (
    <SearchContainer>
      <SearchDescription>
        Let us know what you're currently working on and we'll help you out.
        Whether it's a question about your clinical trial design, device
        development or a time consuming report you need to complete, we're here
        to assist you.
      </SearchDescription>

      <SearchInputWrapper>
        <InputContainer>
          <StyledTextArea
            ref={inputRef}
            autoFocus
            tabIndex={0}
            autoSize={{ minRows: 1, maxRows: 3 }}
            placeholder="What would you like to do today?"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onPressEnter={(e) => {
              if (searchQuery.length > 0 && !e.shiftKey) {
                void handleSubmit(searchQuery);
                e.preventDefault();
              }
            }}
          />
        </InputContainer>
        <Button
          type="primary"
          loading={isSearching}
          disabled={!searchQuery.length}
          onClick={() => {
            if (searchQuery.length > 0) {
              void handleSubmit(searchQuery);
            }
          }}
          icon={isSearching ? null : <SearchOutlined />}
          style={{ height: '36px', minWidth: '80px' }}
        >
          Search
        </Button>
      </SearchInputWrapper>

      {searchQuery.length === 0 && (
        <div style={{ width: '100%', marginTop: '12px' }}>
          <TitleWithInfoContainer>
            <SuggestedSearchesTitle>Suggested searches</SuggestedSearchesTitle>
            <Tooltip title="These suggestions are based on past conversations from the project">
              <InfoCircleOutlined
                style={{ color: token.colorTextQuaternary, fontSize: '12px' }}
              />
            </Tooltip>
          </TitleWithInfoContainer>
          {isLoading ? (
            <SuggestedSearchesContainer>
              {[75, 90, 80].map((width, index) => (
                <SkeletonWrapper key={index}>
                  <Skeleton.Button
                    active
                    size="small"
                    block
                    style={{
                      width: `${width}%`,
                      height: '28px',
                      borderRadius: token.borderRadius,
                    }}
                  />
                </SkeletonWrapper>
              ))}
            </SuggestedSearchesContainer>
          ) : (
            <SuggestedSearchesContainer>
              {suggestedSearches.map((suggestion, index) => (
                <SuggestedSearchButton
                  key={index}
                  onClick={() => handleSuggestedSearchClick(suggestion)}
                  style={{
                    marginBottom: token.marginXS,
                    padding: `${token.paddingXXS}px ${token.paddingSM}px`,
                  }}
                >
                  {suggestion}
                </SuggestedSearchButton>
              ))}
            </SuggestedSearchesContainer>
          )}
        </div>
      )}

      {searchQuery.length > 0 && filteredSuggestions.length > 0 && (
        <div style={{ width: '100%', marginTop: '12px' }}>
          <TitleWithInfoContainer>
            <SuggestedSearchesTitle>
              Autocomplete suggestions
            </SuggestedSearchesTitle>
            <Tooltip title="These suggestions are based on past conversations from the project">
              <InfoCircleOutlined
                style={{ color: token.colorTextQuaternary, fontSize: '12px' }}
              />
            </Tooltip>
          </TitleWithInfoContainer>
          {isLoading ? (
            <SuggestedSearchesContainer>
              {[85, 70, 60].map((width, index) => (
                <SkeletonWrapper key={index}>
                  <Skeleton.Button
                    active
                    size="small"
                    block
                    style={{
                      width: `${width}%`,
                      height: '28px',
                      borderRadius: token.borderRadius,
                    }}
                  />
                </SkeletonWrapper>
              ))}
            </SuggestedSearchesContainer>
          ) : (
            <SuggestedSearchesContainer>
              {filteredSuggestions.map((suggestion, index) => (
                <Button
                  key={index}
                  onClick={() => handleSuggestedSearchClick(suggestion)}
                  style={{
                    marginBottom: token.marginXS,
                    fontSize: '13px',
                    height: 'auto',
                    padding: `${token.paddingXXS}px ${token.paddingSM}px`,
                    width: 'fit-content',
                  }}
                >
                  {suggestion}
                </Button>
              ))}
            </SuggestedSearchesContainer>
          )}
        </div>
      )}

      {(recentConversation && recentMessage) ||
      recentConversationQuery.isLoading ||
      recentConversationQuery.isFetching ||
      recentMessageQuery.isLoading ||
      recentMessageQuery.isFetching ? (
        <div style={{ width: '100%', marginTop: '24px' }}>
          <TitleWithInfoContainer>
            <SuggestedSearchesTitle>
              Continue where you left off
            </SuggestedSearchesTitle>
            <Tooltip title="Your most recent conversation in this project">
              <InfoCircleOutlined
                style={{ color: token.colorTextQuaternary, fontSize: '12px' }}
              />
            </Tooltip>
          </TitleWithInfoContainer>
          {recentConversationQuery.isLoading ||
          recentConversationQuery.isFetching ||
          recentMessageQuery.isLoading ||
          recentMessageQuery.isFetching ? (
            <SkeletonWrapper>
              <Skeleton.Button
                active
                size="large"
                block
                style={{
                  width: '100%',
                  height: '80px',
                  borderRadius: token.borderRadius,
                  marginTop: token.marginXS,
                }}
              />
            </SkeletonWrapper>
          ) : recentConversation && recentMessage ? (
            <RecentConversationCard
              onClick={() =>
                navigate(
                  `/team/${teamId}/project/${projectId}/conversations/${recentConversation.conversationId}`,
                )
              }
            >
              <div style={{ width: '100%' }}>
                <RecentConversationTitle>
                  {recentConversation.title}
                </RecentConversationTitle>
                <RecentConversationPreview>
                  {recentMessage.content.text}
                </RecentConversationPreview>
              </div>
              <ArrowRightOutlined style={{ color: token.colorPrimary }} />
            </RecentConversationCard>
          ) : (
            <SkeletonWrapper>
              <Skeleton.Button
                active
                size="large"
                block
                style={{
                  width: '100%',
                  height: '80px',
                  borderRadius: token.borderRadius,
                  marginTop: token.marginXS,
                }}
              />
            </SkeletonWrapper>
          )}
        </div>
      ) : null}

      <div
        style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}
      >
        <Typography.Text type="secondary" style={{ fontSize: '0.6rem' }}>
          Results may vary. Always verify important scientific information.
        </Typography.Text>
      </div>
    </SearchContainer>
  );
};

const TeamHomePage: React.FC = () => {
  const { userId } = useSessionUserId();

  const teamId = useTeamId();
  const projectId = useProjectId();

  // Session user query
  const sessionUserQuery = useUserQuery(userId);
  const sessionUser = sessionUserQuery.data;

  // Projects query
  const projectsQuery = useProjectsQuery(
    { teamIds: teamId ? [teamId] : [] },
    !!teamId,
  );
  const projects = projectsQuery.data ?? [];

  const content = useMemo(() => {
    // Having the team selected is mandatory otherwise the user is on the team creation page
    if (!teamId || !sessionUser) {
      return <ContentContainer.Loading />;
    }

    // If no project is selected
    if (!projectId) {
      if (!projects.length) {
        return <ProjectSetupContent teamId={teamId} />;
      }
      return <ProjectSelectionContent teamId={teamId} />;
    }

    // Otherwise continue to default content
    return <DefaultContent teamId={teamId} projectId={projectId} />;
  }, [teamId, sessionUser, projectId, projects.length]);

  // Customize text to time of the day
  const currentHour = new Date().getHours();
  const welcomeText =
    (currentHour < 12 && 'Good morning') ||
    (currentHour < 17 && 'Good afternoon') ||
    'Good evening';

  return (
    <ContentContainer showFullWidthToggle={false}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          paddingTop: 'clamp(2rem, 6vh, 8vh)',
          paddingLeft: '1rem',
          paddingRight: '1rem',
          overflow: 'auto',
        }}
      >
        <WelcomeSection>
          <TitleContainer>
            <Title
              level={2}
              style={{ lineHeight: '34px', marginBottom: '0px' }}
            >
              {welcomeText}
              {sessionUser ? (
                <GradientText $isAnimated style={{ marginLeft: '0.5rem' }}>
                  {sessionUser?.fullName.split(' ')[0]}
                </GradientText>
              ) : (
                <Skeleton.Input active style={{ marginLeft: '0.5rem' }} />
              )}
              {','}
            </Title>
          </TitleContainer>
        </WelcomeSection>
        {content}
      </div>
    </ContentContainer>
  );
};

export default TeamHomePage;
